import { useMemo } from "react";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type {
	AutoDispatchGlobalAccessorials,
	CustomerCarrier,
	GlobalCarrier,
} from "~/shared/schemas/auto-dispatch-schema.ts";

import { accessorialsColumn } from "./columns/accessorials-column.tsx";
import {
	autoDispatchColumn,
	makeHandleAutoDispatchChange,
} from "./columns/auto-dispatch-column.tsx";
import { carrierNameColumn } from "./columns/carrier-name-column.tsx";
import { dispatchDaysColumn } from "./columns/dispatch-days-column.tsx";
import { dispatchTimeColumn } from "./columns/dispatch-time-column.tsx";
import {
	loaderCellWrapper,
	passthroughCellWrapper,
} from "./columns/with-loader.tsx";

export type { column, ColumnGroup } from "./columns/types.ts";

export const useGetColumns = ({
	isLoading,
	updateCarrier,
	updateCustomerCarrier,
	isCustomerGrid = false,
	globalAccessorials = [],
}: {
	isLoading: boolean;
	updateCarrier?: (carrier: GlobalCarrier) => void;
	updateCustomerCarrier?: (carrier: CustomerCarrier) => Promise<void>;
	isCustomerGrid?: boolean;
	globalAccessorials?: AutoDispatchGlobalAccessorials;
}) => {
	const { showError } = useNotificationActions();

	// Memoize the columns array so its identity (and every cell function
	// inside it) is stable across re-renders. Without this, every state
	// change in the parent (filter apply/clear, data refetch, etc.) returns
	// a brand-new columns array, causing Kendo Grid to remount every cell on
	// every row — which is very noticeable with heavy custom cells
	// (TimePicker, DropDownList, MultiSelect).
	return useMemo(() => {
		const cellWrapper = isLoading ? loaderCellWrapper : passthroughCellWrapper;

		const handleAutoDispatchChange = makeHandleAutoDispatchChange({
			isCustomerGrid,
			updateCarrier,
			updateCustomerCarrier,
			showError,
		});

		return [
			carrierNameColumn(cellWrapper),
			accessorialsColumn(cellWrapper, globalAccessorials, updateCarrier),
			dispatchTimeColumn(cellWrapper, updateCarrier),
			dispatchDaysColumn(cellWrapper, updateCarrier),
			autoDispatchColumn(cellWrapper, handleAutoDispatchChange),
		];
	}, [
		isLoading,
		isCustomerGrid,
		updateCarrier,
		updateCustomerCarrier,
		globalAccessorials,
		showError,
	]);
};
